import { useCallback, useEffect, useRef, useState } from "react";
import {
  isStayAwakeActive,
  setStayAwakeMode,
  STAY_AWAKE_CHANNEL,
} from "../utils/stayAwakeMode";

// Screen Wake Lock API types (not in all TS DOM libs yet)
interface WakeLockSentinel extends EventTarget {
  readonly released: boolean;
  readonly type: "screen";
  release(): Promise<void>;
}

/**
 * Global "Stay Awake" hook.
 *
 * Manages:
 *  - A boolean toggle persisted in localStorage
 *  - A Screen Wake Lock sentinel (acquired when ON, released when OFF)
 *  - Cross-tab synchronisation via BroadcastChannel
 *
 * Returns `{ isActive, toggle, isSupported }`.
 */
export function useStayAwake() {
  const [isActive, setIsActive] = useState<boolean>(isStayAwakeActive);
  const [isSupported, setIsSupported] = useState<boolean>(false);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);

  // ── Feature detection ──────────────────────────────────────────────
  useEffect(() => {
    setIsSupported("wakeLock" in navigator);
  }, []);

  // ── Acquire / release wake lock ────────────────────────────────────
  const acquireWakeLock = useCallback(async () => {
    if (!("wakeLock" in navigator)) return;
    try {
      const wl = (navigator as any).wakeLock;
      wakeLockRef.current = await wl.request("screen");
      wakeLockRef.current!.addEventListener("release", () => {
        wakeLockRef.current = null;
      });
    } catch {
      // Request can fail when battery is low or page is hidden.
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current && !wakeLockRef.current.released) {
      try {
        await wakeLockRef.current.release();
      } catch {
        /* ignore */
      }
    }
    wakeLockRef.current = null;
  }, []);

  // ── Re-acquire on visibility change (browser releases when hidden) ─
  useEffect(() => {
    const handleVisibility = async () => {
      if (!document.hidden && isActive && !wakeLockRef.current) {
        await acquireWakeLock();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [isActive, acquireWakeLock]);

  // ── Sync wake lock with isActive state ─────────────────────────────
  useEffect(() => {
    if (isActive) {
      acquireWakeLock();
    } else {
      releaseWakeLock();
    }
    return () => {
      releaseWakeLock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // ── Cross-tab sync via BroadcastChannel ────────────────────────────
  useEffect(() => {
    channelRef.current = new BroadcastChannel(STAY_AWAKE_CHANNEL);

    channelRef.current.onmessage = (event: MessageEvent) => {
      if (event.data?.type === "stay_awake_changed") {
        const enabled = event.data.enabled as boolean;
        setIsActive(enabled);
      }
    };

    return () => {
      channelRef.current?.close();
    };
  }, []);

  // ── Toggle callback ────────────────────────────────────────────────
  const toggle = useCallback(() => {
    setIsActive((prev) => {
      const next = !prev;
      setStayAwakeMode(next);
      try {
        channelRef.current?.postMessage({
          type: "stay_awake_changed",
          enabled: next,
        });
      } catch {
        /* channel might be closed */
      }
      return next;
    });
  }, []);

  return { isActive, toggle, isSupported } as const;
}
